// Item upgrades "+N" (krug 5, F9): the player puts materials into an item and raises it to
// +1, +2… (not to be mixed up with the item level, which comes from the zone). Each +1 adds
// 2% to the headline stat (accessories have none: 2% to every affix, F10); at +5, +10, +15
// and +20 one of the item's affixes (picked then, remembered in `affixes[].boost`) gets stronger.
// 6. 10. 2026 (owner, rule B, corrected before its deploy): the first pick gives the more of the top of
// its range and +10% of its rolled value (no +1 on an attribute), each further pick adds 10%, the jump
// to the top at most 1.2× the top, never less than 10% a pick (`pickedAffix` in itemgen.ts; picks made
// before count by this rule too, and none is weaker).
// 30. 9. 2026 (pilot): armor grows every affix by 2% per +1 too (on top of its defense), and the
// cost follows the item's tier (T1 ×0.4 … T4 ×1.3), so a T1 item is worth upgrading early on.
// No failure, never destroyed. Workshop levels cap how far an item can go; the job runs in
// the Workshop queue.

import { TIER_LEVELS, headlineStat, itemTemplate, itemTier } from '../content/items';
import { STACKS } from '../content/resources';
import { isMeal, mealTierFood } from '../meals';
import { GAME } from '../constants';
import { Rng, clamp } from '../rng';
import type { GearSlot, ItemInstance, ItemStats, Rarity, StackKind, StackMap } from '../types';

export const UPGRADE_RULES = {
  /** Highest +N per rarity. */
  max: { common: 5, uncommon: 7, rare: 10, epic: 15, legendary: 20 } as Record<Rarity, number>,
  /** Headline stat per +1. */
  perLevel: 0.02,
  milestones: [5, 10, 15, 20] as readonly number[],
  /**
   * Every pick adds at least this share of the affix's rolled value (the rule before 6. 10. 2026, now the floor of rule B;
   * a Pristine affix and an old one with no range today: exactly this).
   */
  milestoneBoost: 0.1,
  /** 6. 10. 2026 (rule B): the jump to the top of the range takes an affix at most this far over the top. */
  milestoneCap: 1.2,
  /**
   * 6. 10. 2026 (rule B): the first pick on an attribute adds at least this much. 0 (owner, 6. 10. 2026): with 1 the Power of
   * a level 20 Brawler grew 13% (the rule's limit 10%); the pick list says instead when a pick does not count in a fight.
   */
  milestoneAttrMin: 0,
  /** Workshop 1 allows up to +4, each further level 4 more (Workshop 5: +20). */
  perWorkshopLevel: 4,
  /** Cost multiplier per rarity. */
  rarityMult: { common: 1, uncommon: 1.3, rare: 1.7, epic: 2.2, legendary: 3 } as Record<Rarity, number>,
  /**
   * 30. 9. 2026: cost multiplier per item tier, on Scrap and the item's material (the Electronics or
   * Chemicals from +10 and the Relic Shards from +16 stay as they are).
   */
  tierMult: { 1: 0.4, 2: 0.7, 3: 1, 4: 1.3 } as Record<1 | 2 | 3 | 4, number>,
  /** 30. 9. 2026: slots whose affixes grow with every +1, like an accessory's (armor keeps its defense +2% as well). */
  affixSlots: ['helmet', 'body', 'gloves', 'pants', 'boots', 'accessory'] as readonly GearSlot[],
  /** Dismantling gives back this share of what went in (rounded down). */
  refund: 0.5,
} as const;

/** The material of the item's kind, as for repairs: Metal for weapons and hard armor, Cloth for clothes and packs. */
export function upgradeMaterial(slot: GearSlot): StackKind {
  if (slot === 'weapon' || slot === 'helmet' || slot === 'boots') return 'metal';
  if (slot === 'accessory') return 'electronics';
  return 'cloth';
}

/**
 * Upgrade jobs share the Workshop queue: their recipe id is `upgrade:<itemId>`. Krug 5 (K4.14):
 * at a milestone the affix the player picked rides along as `upgrade:<itemId>#<index>`, so the
 * job remembers it without a column of its own.
 */
export const UPGRADE_JOB = 'upgrade:';
export const upgradeJobId = (itemId: string, affix?: number | null) => `${UPGRADE_JOB}${itemId}${affix != null ? `#${affix}` : ''}`;
export const upgradeJobItem = (recipeId: string): string | null => (recipeId.startsWith(UPGRADE_JOB) ? recipeId.slice(UPGRADE_JOB.length).split('#')[0] : null);
export const upgradeJobChoice = (recipeId: string): number | null => {
  const m = /#(\d+)$/.exec(recipeId);
  return recipeId.startsWith(UPGRADE_JOB) && m ? Number(m[1]) : null;
};

/** The next +N is a milestone (+5, +10, +15, +20): the player picks an affix to boost (rule B, `pickedAffix`). */
export const isMilestone = (n: number) => UPGRADE_RULES.milestones.includes(n);

/** How far this item can go with this Workshop level. */
export function upgradeCap(rarity: Rarity, workshopLevel: number): number {
  return Math.min(UPGRADE_RULES.max[rarity], UPGRADE_RULES.perWorkshopLevel * Math.max(1, workshopLevel));
}

/** The Workshop level needed to reach +n. */
export function workshopFor(n: number): number {
  return Math.ceil(n / UPGRADE_RULES.perWorkshopLevel);
}

/** What going from +n to +(n+1) costs (30. 9. 2026: Scrap and the material also by the item's tier). */
export function upgradeCost(item: Pick<ItemInstance, 'templateId' | 'rarity' | 'tier'>, n: number): StackMap {
  const t = itemTemplate(item.templateId);
  const m = UPGRADE_RULES.rarityMult[item.rarity] * UPGRADE_RULES.tierMult[itemTier(item)];
  const slot = t.slot;
  const out: StackMap = {};
  const add = (k: StackKind, q: number) => {
    if (q > 0) out[k] = (out[k] ?? 0) + q;
  };
  add('scrap', Math.max(1, Math.round(10 * Math.pow(n + 1, 1.5) * m)));
  add(upgradeMaterial(slot), Math.max(1, Math.round(3 * (n + 1) * m)));
  if (n + 1 >= 10) add(slot === 'weapon' || slot === 'accessory' ? 'electronics' : 'chemicals', n - 8);
  if (n + 1 >= 16) add('relic_shard', n - 14);
  return out;
}

/**
 * Krug 5 (K4.7): INT makes crafting and upgrades cheaper in materials: −1% per point of
 * effective INT over 15, at most −30%. Scrap, specials (Blueprints, Relic Shards) and time do not change.
 */
/** Krug 5 (K4.7): the INT numbers; mutable only for the simulations. */
export const INT_CRAFT = { cutPerPoint: 0.01, cutMax: 0.3, rollPerPoint: 0.02, rollMax: 0.5 };

export function intMaterialCut(int: number): number {
  return clamp((int - GAME.BASE_ATTR) * INT_CRAFT.cutPerPoint, 0, INT_CRAFT.cutMax);
}

/** Krug 5 (K4.7): every affix of an item made in the Workshop or the Relic Forge rolls at least this well: 2% per INT point over 15, at most 50%. */
export function intMinRoll(int: number): number {
  return clamp((int - GAME.BASE_ATTR) * INT_CRAFT.rollPerPoint, 0, INT_CRAFT.rollMax);
}

/**
 * Krug 5 (K10.3): the lowest affix roll of a crafted item with the skill tree's points on top
 * (Machinist, from the tree): (INT − 15) × 2% + points, at most 50% + points (55% with Machinist +5).
 */
export function craftMinRoll(int: number, treePts = 0): number {
  return clamp((int - GAME.BASE_ATTR) * INT_CRAFT.rollPerPoint + treePts / 100, 0, INT_CRAFT.rollMax + treePts / 100);
}

/**
 * Krug 5 (K10.3): a recipe's cost with the INT cut and then the skill tree's change of materials
 * (`craftMaterials`, −0.2 = 20% less): rounded up, never below 1 of a material. Scrap and specials stay.
 */
export function recipeCost(cost: StackMap, intCut: number, treeMaterials = 0, mealFood = 0): StackMap {
  const out = cutCost(cost, intCut);
  if (treeMaterials) {
    for (const [k, v] of Object.entries(out) as [StackKind, number][]) {
      if (!v || k === 'scrap' || STACKS[k]?.category === 'special') continue;
      out[k] = Math.max(1, Math.ceil(v * (1 + treeMaterials) - 1e-9));
    }
  }
  // Krug 5 (K12.4): Campfire takes Food off meal recipes only (the caller passes 0 for anything else).
  if (mealFood && out.food) out.food = Math.max(1, Math.ceil(out.food * (1 + mealFood) - 1e-9));
  return out;
}

/**
 * Krug 5 (K12.4): a recipe's cost for this survivor: INT, the tree's materials and, for a meal, Campfire. Package 4,
 * part O: a meal's Food by the survivor's tier (T4 twice T1; `level` missing: T1).
 */
export function recipeCostFor(r: { cost: StackMap; output: { kind: 'item' } | { kind: 'stack'; stack: StackKind } }, stats: { int: number; craftMaterials?: number; mealFood?: number; level?: number }): StackMap {
  const meal = r.output.kind === 'stack' && isMeal(r.output.stack);
  const cost = meal && r.cost.food && stats.level ? { ...r.cost, food: Math.round(r.cost.food * mealTierFood(wearableTier(stats.level))) } : r.cost;
  return recipeCost(cost, intMaterialCut(stats.int), stats.craftMaterials ?? 0, meal ? (stats.mealFood ?? 0) : 0);
}

/** A cost with the INT cut on its materials (never below 1 of a material it asks for). */
export function cutCost(cost: StackMap, cut: number): StackMap {
  const out: StackMap = {};
  for (const [k, v] of Object.entries(cost) as [StackKind, number][]) {
    if (!v) continue;
    out[k] = cut > 0 && k !== 'scrap' && STACKS[k]?.category !== 'special' ? Math.max(1, Math.round(v * (1 - cut))) : v;
  }
  return out;
}

/** Minutes of the job from +n to +(n+1) (before crafting speed). */
export function upgradeMinutes(n: number): number {
  return (n + 1) * 3;
}

/** Everything that went into the item up to its current +N. */
export function upgradeInvested(item: Pick<ItemInstance, 'templateId' | 'rarity' | 'upgrade' | 'tier'>): StackMap {
  const out: StackMap = {};
  for (let n = 0; n < (item.upgrade ?? 0); n++) for (const [k, v] of Object.entries(upgradeCost(item, n)) as [StackKind, number][]) out[k] = (out[k] ?? 0) + v;
  return out;
}

/** Half of what went in, rounded down, returned when the item is dismantled. */
export function upgradeRefund(item: Pick<ItemInstance, 'templateId' | 'rarity' | 'upgrade' | 'tier'>): StackMap {
  const out: StackMap = {};
  for (const [k, v] of Object.entries(upgradeInvested(item)) as [StackKind, number][]) {
    const q = Math.floor(v * UPGRADE_RULES.refund);
    if (q > 0) out[k] = q;
  }
  return out;
}

/**
 * Base stats with the upgrade on: the headline stat (damage, armor, capacity) +2% per level.
 * Accessories have no headline stat, so all their base stats grow.
 */
export function upgradedBase(item: Pick<ItemInstance, 'templateId' | 'base' | 'upgrade'>): ItemStats {
  const n = item.upgrade ?? 0;
  if (!n) return item.base;
  const mult = 1 + UPGRADE_RULES.perLevel * n;
  const head = headlineStat(itemTemplate(item.templateId).slot);
  const out: ItemStats = { ...item.base };
  for (const k of Object.keys(out) as (keyof ItemStats)[]) if (head ? k === head : true) out[k] = (out[k] ?? 0) * mult;
  return out;
}

/** 30. 9. 2026: the slot's affixes grow with every +1 (armor and accessories; weapons and packs do not). */
export const affixesGrow = (slot: GearSlot) => UPGRADE_RULES.affixSlots.includes(slot);

/**
 * Accessories (no headline stat, F10) and, from 30. 9. 2026, armor: every affix grows 2% per level,
 * from the upgrade level alone (items upgraded before get it too). Weapons and backpacks: ×1.
 */
export function affixUpgradeMult(item: Pick<ItemInstance, 'templateId' | 'upgrade'>): number {
  const n = item.upgrade ?? 0;
  if (!n || !affixesGrow(itemTemplate(item.templateId).slot)) return 1;
  return 1 + UPGRADE_RULES.perLevel * n;
}

/**
 * The item one level up: +1, and at a milestone one affix is picked (`affixes[].boost`). Krug 5 (K4.14): `choice`
 * is the affix the player picked (the server passes the best roll when there is none); without
 * it (old callers, simulations) a random one.
 */
export function applyUpgrade<T extends Pick<ItemInstance, 'upgrade' | 'affixes'>>(item: T, rng: Rng, choice?: number | null): T {
  const next = (item.upgrade ?? 0) + 1;
  const affixes = item.affixes.map((a) => ({ ...a }));
  if (isMilestone(next) && affixes.length) {
    const i = choice != null && choice >= 0 && choice < affixes.length ? choice : rng.int(0, affixes.length - 1);
    affixes[i].boost = (affixes[i].boost ?? 0) + 1;
  }
  return { ...item, upgrade: next, affixes };
}

// ---- Tier upgrade (1. 10. 2026, C4) ---------------------------------------------------------------
// A Rare, Epic or Legendary item at +5 or more goes up one tier, at most to the tier its survivor may wear
// (T2 from level 6, T3 from 11, T4 from 16). Rarity, perk, affixes and their rolls stay: the headline stat
// and the affixes are worked out again for the new tier with the same roll (items/tierUp.ts), the item level
// becomes at least the new tier's first level, +N stays. The cost: Relic Shards by rarity and one +N step
// of the new tier (the step from +5: Scrap and the item's material), a job in the Workshop (T3 needs
// Workshop 3, T4 Workshop 4). Dismantling gives back half of what went in, as for +N.

export type Tier = 1 | 2 | 3 | 4;

export const TIER_UP_RULES = {
  /** False (simulations, VARIANT=a and b): no tier upgrades, and the Relic Forge makes the template's tier. */
  on: true,
  rarities: ['rare', 'epic', 'legendary'] as Rarity[],
  /** The item must be at least this +N. */
  minUpgrade: 5,
  shards: { rare: 5, epic: 10, legendary: 15 } as Partial<Record<Rarity, number>>,
  /** The materials are one +N step on the new tier: the step from this +N (Scrap and the item's material). */
  step: 5,
  /** Workshop level and minutes by the tier the item goes to. */
  workshop: { 2: 2, 3: 3, 4: 4 } as Record<2 | 3 | 4, number>,
  minutes: { 2: 30, 3: 60, 4: 120 } as Record<2 | 3 | 4, number>,
  /** Dismantling gives back this share of what went in (rounded down), as for +N. */
  refund: 0.5,
};

/** Tier upgrade jobs share the Workshop queue with crafts and +N: recipe id `tierup:<itemId>`. */
export const TIERUP_JOB = 'tierup:';
export const tierUpJobId = (itemId: string) => `${TIERUP_JOB}${itemId}`;
export const isTierUpJob = (recipeId: string) => recipeId.startsWith(TIERUP_JOB);
/** The item of an upgrade or tier upgrade job (null for a recipe). */
/**
 * 4. 10. 2026 (package 3, H): a Rework job, "rework:<item>:<donor>.<donor>": the item and the donors it takes (they
 * wait out of sight until the job ends; a cancel gives them back).
 */
export const REWORK_JOB = 'rework:';
export const reworkJobId = (itemId: string, donors: readonly string[]) => `${REWORK_JOB}${itemId}:${donors.join('.')}`;
export const isReworkJob = (recipeId: string) => recipeId.startsWith(REWORK_JOB);
export function parseReworkJob(recipeId: string): { itemId: string; donors: string[] } | null {
  if (!isReworkJob(recipeId)) return null;
  const [itemId, donors] = recipeId.slice(REWORK_JOB.length).split(':');
  return itemId ? { itemId, donors: donors ? donors.split('.').filter(Boolean) : [] } : null;
}

/**
 * 7. 10. 2026 (owner): the repair of a broken item, "repair:<item>:<what was paid>" ("scrap=104,metal=12,relic_shard=2"):
 * a Workshop job on one bench; the item waits on the bench (location `repair`, its `slot` kept when it was worn) and
 * comes back whole. A cancel gives back what was paid (the price goes by the gear worn, and the item came off).
 */
export const REPAIR_JOB = 'repair:';
export const repairJobId = (itemId: string, paid?: StackMap) =>
  `${REPAIR_JOB}${itemId}${paid ? `:${(Object.entries(paid) as [string, number][]).filter(([, v]) => v > 0).map(([k, v]) => `${k}=${v}`).join(',')}` : ''}`;
export const isRepairJob = (recipeId: string) => recipeId.startsWith(REPAIR_JOB);
export function parseRepairJob(recipeId: string): { itemId: string; paid: StackMap | null } | null {
  if (!isRepairJob(recipeId)) return null;
  const [itemId, rest] = recipeId.slice(REPAIR_JOB.length).split(':');
  if (!itemId) return null;
  if (rest == null) return { itemId, paid: null };
  const paid: StackMap = {};
  for (const part of rest.split(',').filter(Boolean)) {
    const [k, v] = part.split('=');
    const n = Number(v);
    if (k && Number.isFinite(n) && n > 0) paid[k as keyof StackMap] = n;
  }
  return { itemId, paid };
}

export const itemJobItem = (recipeId: string): string | null =>
  isTierUpJob(recipeId) ? recipeId.slice(TIERUP_JOB.length) : isReworkJob(recipeId) ? (parseReworkJob(recipeId)?.itemId ?? null) : isRepairJob(recipeId) ? (parseRepairJob(recipeId)?.itemId ?? null) : upgradeJobItem(recipeId);

/** The highest tier a survivor of this level may wear (T1 to level 5, T2 from 6, T3 from 11, T4 from 16). */
export function wearableTier(level: number): Tier {
  return ([4, 3, 2, 1] as Tier[]).find((t) => level >= TIER_LEVELS[t][0]) ?? 1;
}

/** What one tier upgrade to `to` costs: Relic Shards by rarity and one +N step of the new tier. */
export function tierUpCost(item: Pick<ItemInstance, 'templateId' | 'rarity'>, to: Tier): StackMap {
  const step = upgradeCost({ templateId: item.templateId, rarity: item.rarity, tier: to }, TIER_UP_RULES.step);
  const out: StackMap = {};
  const shards = TIER_UP_RULES.shards[item.rarity] ?? 0;
  if (shards) out.relic_shard = shards;
  if (step.scrap) out.scrap = step.scrap;
  const mat = upgradeMaterial(itemTemplate(item.templateId).slot);
  if (step[mat]) out[mat] = step[mat];
  return out;
}

/**
 * Everything the tier upgrades of an item cost, from its template's tier to its own. A Relic Forge item
 * (`accountBound`) counts none: the Forge gives the survivor's tier, and the item is made of shards.
 */
export function tierUpInvested(item: Pick<ItemInstance, 'templateId' | 'rarity' | 'tier' | 'rework'> & { accountBound?: boolean; crafted?: boolean }): StackMap {
  const out: StackMap = {};
  if (item.accountBound) return out;
  // 5. 10. 2026 (package 3, I): a crafted item is made on the survivor's tier; only the tier upgrades it got after that
  // were paid (recorded when they finish). Until then its dismantling gave back tier upgrades nobody paid for.
  if (item.crafted) return { ...(item.rework?.tierPaid ?? {}) };
  const from = itemTemplate(item.templateId).tier;
  for (let t = from + 1; t <= itemTier(item); t++)
    for (const [k, v] of Object.entries(tierUpCost(item, t as Tier)) as [StackKind, number][]) out[k] = (out[k] ?? 0) + v;
  return out;
}

/** Half of what the tier upgrades cost, rounded down, given back when the item is dismantled. */
export function tierUpRefund(item: Pick<ItemInstance, 'templateId' | 'rarity' | 'tier' | 'rework'> & { accountBound?: boolean; crafted?: boolean }): StackMap {
  const out: StackMap = {};
  for (const [k, v] of Object.entries(tierUpInvested(item)) as [StackKind, number][]) {
    const q = Math.floor(v * TIER_UP_RULES.refund);
    if (q > 0) out[k] = q;
  }
  return out;
}

/**
 * Why this item cannot go up a tier now (null: it can), for a survivor of `level` and a Workshop of
 * `workshop`. The next tier comes in `to`.
 */
export function tierUpBlocker(item: Pick<ItemInstance, 'templateId' | 'rarity' | 'tier' | 'upgrade'>, level: number, workshop: number): { to: Tier | null; reason: string | null } {
  const cur = itemTier(item);
  const to = cur < 4 ? ((cur + 1) as Tier) : null;
  if (!TIER_UP_RULES.rarities.includes(item.rarity)) return { to: null, reason: 'Only Rare, Epic and Legendary items can go up a tier.' };
  if (!to) return { to: null, reason: 'Already the top tier.' };
  if (to > wearableTier(level)) return { to, reason: `T${to} from level ${TIER_LEVELS[to][0]}` };
  if ((item.upgrade ?? 0) < TIER_UP_RULES.minUpgrade) return { to, reason: `Upgrade it to +${TIER_UP_RULES.minUpgrade} first.` };
  const ws = TIER_UP_RULES.workshop[to as 2 | 3 | 4];
  if (workshop < ws) return { to, reason: `Workshop ${ws} needed` };
  return { to, reason: null };
}
